import React, { FC, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Modal,
  Popconfirm,
  Segmented,
  Tabs,
} from "antd";
import {
  AppstoreOutlined,
  BarsOutlined,
  DeleteOutlined,
  FilterOutlined,
  FolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import SearchInput from "../../../form-components/SearchComponent";
import { AddAccess } from "../../../../components/accessControl/AccessControl";
import { updateAllProjectsListViewTableColumnsConfig } from "../../../../store/slices/projectManagement/all-projects/allProjectsListViewTableColumnsConfig";
import ProjectCreation from "../projectCreation";
import { ALL_STATUSES } from "./statusMeta";
import { useProjectList } from "./useProjectList";
import { useOpenProject } from "./useOpenProject";
import { buildProjectColumns } from "./ProjectsTable";
import ProjectCardGrid from "./ProjectCardGrid";
import ProjectListRows from "./ProjectListRows";
import ProjectsTable from "./ProjectsTable";
import ProjectFilterDrawer from "./ProjectFilterDrawer";
import "./hub.css";

const STATUS_TABS = ["All", ...ALL_STATUSES];
const VIEWS = ["grid", "list", "table"];
const VIEW_STORAGE_KEY = "pmsProjectsView";

const ProjectsHub: FC = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get("status");
  const statusTab = STATUS_TABS.includes(statusParam || "")
    ? (statusParam as string)
    : "Active";

  const viewParam = searchParams.get("view");
  const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
  const view = VIEWS.includes(viewParam || "")
    ? (viewParam as string)
    : VIEWS.includes(storedView || "")
    ? (storedView as string)
    : "grid";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const [filteredData, setFilteredData] = useState<any>({});
  const [filterCount, setFilterCount] = useState(0);
  const [filtersModal, setFiltersModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [tableConfigModal, setTableConfigModal] = useState(false);

  const {
    dataSource,
    pagination,
    loading,
    tableOnChange,
    onPageChange,
    onSearch,
    refreshListView,
  } = useProjectList(statusTab, filteredData);

  const openProject = useOpenProject();
  const searchMethods = useForm({ mode: "onChange" });

  const { data: columnsConfigData } = useSelector(
    (state: any) => state.AllProjectsListViewTableColumnsConfig
  );
  const columnTitles = useMemo(
    () =>
      buildProjectColumns(() => {}).map((c: any) => ({
        key: c.key,
        title: c.title,
      })),
    []
  );

  return (
    <>
      <Head title="Projects" />
      <Content>
        <div className="pms-hub">
          <div className="pms-hub-surface">
          {/* Page header */}
          <div className="pms-hub-controls" style={{ marginBottom: 16 }}>
            <div>
              <h3 className="pms-hub-title">Projects</h3>
              <div className="pms-hub-sub">
                {pagination.total
                  ? `${pagination.total} ${
                      statusTab === "All" ? "" : statusTab.toLowerCase() + " "
                    }project${pagination.total === 1 ? "" : "s"}`
                  : " "}
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Link to="/pms/groups-projects" className="pms-hub-quiet-link me-2">
                <FolderOutlined /> Groups
              </Link>
              <Link to="/pms/recycle-bin" className="pms-hub-quiet-link me-2">
                <DeleteOutlined /> Recycle Bin
              </Link>
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshListView}
                title="Refresh"
              />
              <Badge count={filterCount} size="small">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersModal(true)}
                >
                  Filters
                </Button>
              </Badge>
              {view === "table" && (
                <Popconfirm
                  title={<></>}
                  icon={<></>}
                  placement="leftTop"
                  showCancel={false}
                  open={tableConfigModal}
                  okButtonProps={{ style: { display: "none" } }}
                  onOpenChange={(visible) => setTableConfigModal(visible)}
                  description={
                    <div>
                      <div className="form-label">Toggle Columns View</div>
                      <div className="d-flex flex-wrap m-0">
                        <ul className="link-list-opt no-bdr">
                          {Object.keys(columnsConfigData || {})
                            ?.filter((_key) =>
                              columnTitles.some(
                                (col) => col.key === _key && col.title
                              )
                            )
                            ?.map((_key) => (
                              <li
                                key={_key}
                                style={{ width: "190px" }}
                                className={
                                  columnsConfigData[_key] ? "active" : ""
                                }
                              >
                                <a
                                  href="#tableView"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    dispatch(
                                      updateAllProjectsListViewTableColumnsConfig(
                                        { [_key]: !columnsConfigData[_key] }
                                      )
                                    );
                                  }}
                                >
                                  <span>
                                    {
                                      columnTitles.find(
                                        (col) => col.key === _key
                                      )?.title
                                    }
                                  </span>
                                </a>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </div>
                  }
                >
                  <Button icon={<SettingOutlined />} title="Configure columns" />
                </Popconfirm>
              )}
              <AddAccess moduleName="Projects">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setModalOpen(true)}
                >
                  Add Project
                </Button>
              </AddAccess>
            </div>
          </div>

          {/* Status tabs + search + view toggle */}
          <div className="pms-hub-controls">
            <Tabs
              activeKey={statusTab}
              onChange={(key) => setParam("status", key)}
              items={STATUS_TABS.map((s) => ({ key: s, label: s }))}
              tabBarStyle={{ marginBottom: 0 }}
            />
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <FormProvider {...searchMethods}>
                <form onSubmit={(e) => e.preventDefault()}>
                  <SearchInput
                    name="search"
                    placeholder="Search projects..."
                    width={260}
                    prefixIcon={<SearchOutlined />}
                    onDebouncedSearch={onSearch}
                    debounceDelay={300}
                  />
                </form>
              </FormProvider>
              <Segmented
                value={view}
                onChange={(v) => {
                  const value = String(v);
                  localStorage.setItem(VIEW_STORAGE_KEY, value);
                  setParam("view", value);
                }}
                options={[
                  { value: "grid", icon: <AppstoreOutlined />, title: "Grid" },
                  { value: "list", icon: <BarsOutlined />, title: "List" },
                  { value: "table", icon: <TableOutlined />, title: "Table" },
                ]}
              />
            </div>
          </div>

          {/* Content */}
          <div style={{ marginTop: 20 }}>
            {view === "grid" && (
              <ProjectCardGrid
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                statusTab={statusTab}
                onOpen={openProject}
                onPageChange={onPageChange}
                onAddProject={() => setModalOpen(true)}
              />
            )}
            {view === "list" && (
              <ProjectListRows
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                statusTab={statusTab}
                onOpen={openProject}
                onPageChange={onPageChange}
                onAddProject={() => setModalOpen(true)}
              />
            )}
            {view === "table" && (
              <ProjectsTable
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                onOpen={openProject}
                tableOnChange={tableOnChange}
              />
            )}
          </div>
          </div>
        </div>
      </Content>

      <Modal
        maskClosable={false}
        title="New Project Creation"
        style={{ top: 8 }}
        width={"99%"}
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        destroyOnClose={true}
      >
        <ProjectCreation
          onSuccess={() => {
            setModalOpen(false);
            refreshListView();
          }}
        />
      </Modal>

      <ProjectFilterDrawer
        open={filtersModal}
        onClose={() => setFiltersModal(false)}
        statusTab={statusTab}
        onApply={(data, count) => {
          setFilteredData(data);
          setFilterCount(count);
          setFiltersModal(false);
        }}
        onReset={() => {
          setFilteredData({});
          setFilterCount(0);
          setFiltersModal(false);
        }}
      />
    </>
  );
};

export default ProjectsHub;
